import type { FC } from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Label, Spinner, TextInput, Tooltip } from "flowbite-react";
import {
  HiCheck,
  HiOutlineArrowLeft,
  HiOutlineArrowRight,
  HiOutlineCalculator,
  HiOutlineInformationCircle,
  HiOutlineOfficeBuilding,
  HiOutlineSearch,
  HiOutlineViewGrid,
} from "react-icons/hi";
import { Button, ToggleSwitch } from "@components";
import { cn, wizardPanel, Text } from "@design-system";
import { useWorkspace } from "../../context/WorkspaceContext";
import { useCache } from "../../context/CacheContext";
import { useUserSession } from "../../context/UserContext";
import { updateWorkspace } from "../../api";
import { Company } from "../../models";
import { ICompany } from "../../types";

const DEFAULT_WORKSPACE_NAME = "Default workspace";

interface BrregAddress {
  adresse?: string[];
  postnummer?: string;
  poststed?: string;
  landkode?: string;
}

interface BrregCompany {
  navn: string;
  organisasjonsnummer: string;
  registrertIMvaregisteret?: boolean;
  forretningsadresse?: BrregAddress;
  postadresse?: BrregAddress;
}

const stepIds = ["workspace", "company", "accounting"] as const;
type WizardStep = (typeof stepIds)[number];

const StepIllustration: FC<{ step: WizardStep }> = ({ step }) => {
  const icon =
    step === "workspace" ? (
      <HiOutlineViewGrid className="h-9 w-9" />
    ) : step === "company" ? (
      <HiOutlineSearch className="h-9 w-9" />
    ) : (
      <HiOutlineCalculator className="h-9 w-9" />
    );

  return (
    <div
      className="relative hidden h-24 w-32 flex-shrink-0 items-center justify-center overflow-hidden rounded-xl bg-brand-muted text-brand sm:flex"
      aria-hidden="true"
    >
      <span className="absolute -right-5 -top-5 h-16 w-16 rounded-full border border-brand/15" />
      <span className="absolute -bottom-4 -left-3 h-12 w-12 rotate-12 rounded-lg bg-brand/10" />
      <span className="relative flex h-14 w-14 items-center justify-center rounded-xl border border-brand/15 bg-surface shadow-sm">
        {icon}
      </span>
    </div>
  );
};

export const OnboardingWizard: FC = () => {
  const { t } = useTranslation(["onboarding", "common"]);
  const navigate = useNavigate();
  const { activeWorkspace, setActiveWorkspace } = useWorkspace();
  const { updateCacheKey } = useCache();
  const { userSession } = useUserSession();

  const workspaceId = activeWorkspace?.workspaceId ?? "";
  const token = userSession?.token.accessToken ?? "";
  const currentWorkspaceName = activeWorkspace?.workspaceName ?? "";
  const hasCustomName =
    !!currentWorkspaceName && currentWorkspaceName !== DEFAULT_WORKSPACE_NAME;

  const [step, setStep] = useState<WizardStep>("workspace");
  const [workspaceName, setWorkspaceName] = useState(
    hasCustomName ? currentWorkspaceName : "",
  );
  const [company, setCompany] = useState<ICompany>(() => Company.default());
  const [companySearch, setCompanySearch] = useState("");
  const [searchResults, setSearchResults] = useState<BrregCompany[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    const query = companySearch.trim();
    if (query.length < 2 || company.name === query) {
      setSearchResults([]);
      setIsSearching(false);
      setSearchFailed(false);
      return;
    }

    const controller = new AbortController();
    const timeout = window.setTimeout(async () => {
      setIsSearching(true);
      setSearchFailed(false);
      try {
        const response = await fetch(
          `https://data.brreg.no/enhetsregisteret/api/enheter?size=10&navn=${encodeURIComponent(query)}`,
          { signal: controller.signal },
        );
        if (!response.ok) throw new Error(`BRREG returned ${response.status}`);
        const data = await response.json();
        setSearchResults(data._embedded?.enheter ?? []);
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError")
          return;
        console.warn("Onboarding: BRREG search failed", error);
        setSearchResults([]);
        setSearchFailed(true);
      } finally {
        if (!controller.signal.aborted) setIsSearching(false);
      }
    }, 400);

    return () => {
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [company.name, companySearch]);

  const selectCompany = (result: BrregCompany) => {
    const address = result.forretningsadresse ?? result.postadresse;
    setCompany((current) => ({
      ...current,
      name: result.navn,
      organizationNumber: result.organisasjonsnummer,
      primaryAddress: {
        addressLine: address?.adresse?.join(", ") ?? "",
        postCode: address?.postnummer ?? "",
        city: address?.poststed ?? "",
        countryCode: address?.landkode ?? "NO",
      },
      paymentTerms: {
        ...current.paymentTerms,
        isVatRegistered: result.registrertIMvaregisteret === true,
      },
    }));
    setCompanySearch(result.navn);
    setSearchResults([]);
    setSearchFailed(false);
  };

  const updatePaymentTerms = (
    key: keyof ICompany["paymentTerms"],
    value: string | number | boolean,
  ) => {
    setCompany((current) => ({
      ...current,
      paymentTerms: { ...current.paymentTerms, [key]: value },
    }));
  };

  const saveWorkspaceStep = async () => {
    const desiredName = workspaceName.trim();
    if (isSubmitting || !desiredName || !token || !workspaceId) return;

    setIsSubmitting(true);
    setErrorMessage(null);
    try {
      if (currentWorkspaceName !== desiredName) {
        await updateWorkspace(token, workspaceId, { name: desiredName });
        if (activeWorkspace) {
          setActiveWorkspace({
            ...activeWorkspace,
            workspaceName: desiredName,
          });
        }
      }
      setStep("company");
    } catch (error) {
      console.warn("Onboarding: rename workspace failed", error);
      setErrorMessage(t("onboarding:errors.workspace"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const saveCompanyStep = async () => {
    if (isSubmitting || !company.name || !company.organizationNumber) return;
    setIsSubmitting(true);
    setErrorMessage(null);
    try {
      const savedCompany = company.id
        ? await Company.update(new Company(company), company)
        : await Company.create(workspaceId, company);
      setCompany(savedCompany);
      setStep("accounting");
    } catch (error) {
      console.warn("Onboarding: create company failed", error);
      setErrorMessage(t("onboarding:errors.company"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const finishOnboarding = async (saveAccounting: boolean) => {
    if (isSubmitting || !company.id) return;
    setIsSubmitting(true);
    setErrorMessage(null);
    try {
      if (saveAccounting) {
        await Company.update(new Company(company), {
          invoiceEmail: company.invoiceEmail,
          paymentTerms: company.paymentTerms,
          accountingSettings: company.accountingSettings,
        });
      }
      updateCacheKey();
      navigate("/", { replace: true });
    } catch (error) {
      console.warn("Onboarding: save accounting details failed", error);
      setErrorMessage(t("onboarding:errors.accounting"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const stepIndex = stepIds.indexOf(step);

  return (
    <div className="w-full">
      <ol
        className="mb-6 flex items-center rounded-xl bg-brand px-4 pb-3 pt-4 shadow-custom sm:px-6"
        aria-label={t("onboarding:wizard.progress_label")}
      >
        {stepIds.map((stepId, index) => {
          const isComplete = index < stepIndex;
          const isCurrent = index === stepIndex;
          return (
            <li
              key={stepId}
              className={cn(
                "flex items-center",
                index < stepIds.length - 1 && "flex-1",
              )}
            >
              <div className="flex flex-col items-center gap-2">
                <span
                  className={cn(
                    "flex h-9 w-9 items-center justify-center rounded-full border text-sm font-semibold",
                    isComplete && "border-white bg-white text-brand",
                    isCurrent &&
                      "border-white bg-white text-brand ring-4 ring-white/20",
                    !isComplete &&
                      !isCurrent &&
                      "border-white/40 bg-white/10 text-white/70",
                  )}
                  aria-current={isCurrent ? "step" : undefined}
                >
                  {isComplete ? <HiCheck className="h-5 w-5" /> : index + 1}
                </span>
                <span
                  className={cn(
                    "text-xs",
                    isCurrent ? "font-semibold text-white" : "text-white/75",
                  )}
                >
                  {t(`onboarding:wizard.steps.${stepId}.label`)}
                </span>
              </div>
              {index < stepIds.length - 1 ? (
                <span
                  className={cn(
                    "mx-3 mb-6 h-px flex-1",
                    isComplete ? "bg-white" : "bg-white/30",
                  )}
                />
              ) : null}
            </li>
          );
        })}
      </ol>

      <div className={cn(wizardPanel, "p-5 sm:p-8")}>
        {step === "workspace" ? (
          <section>
            <div className="flex items-start justify-between gap-6">
              <div>
                <Text as="h1" variant="section-title-lg">
                  {t("onboarding:wizard.steps.workspace.title")}
                </Text>
                <Text variant="secondary" className="mt-2 text-sm">
                  {t("onboarding:wizard.steps.workspace.description")}
                </Text>
              </div>
              <StepIllustration step="workspace" />
            </div>
            <div className="mt-6">
              <Label
                htmlFor="workspaceName"
                value={t("onboarding:wizard.steps.workspace.input_label")}
              />
              <TextInput
                id="workspaceName"
                className="mt-2"
                value={workspaceName}
                onChange={(event) => setWorkspaceName(event.target.value)}
                placeholder={t("onboarding:wizard.steps.workspace.placeholder")}
                autoFocus
              />
            </div>
            {errorMessage ? (
              <div className="mt-5 rounded-lg border border-danger/30 bg-danger-muted p-3 text-sm text-danger">
                {errorMessage}
              </div>
            ) : null}
            <div className="mt-8 flex justify-end">
              <Button
                variant="primary"
                icon={<HiOutlineArrowRight className="h-4 w-4" />}
                iconPosition="right"
                disabled={!workspaceName.trim() || isSubmitting}
                onClick={() => void saveWorkspaceStep()}
              >
                {isSubmitting
                  ? t("onboarding:wizard.saving")
                  : t("onboarding:wizard.continue")}
              </Button>
            </div>
          </section>
        ) : null}

        {step === "company" ? (
          <section>
            <div className="flex items-start justify-between gap-6">
              <div>
                <Text as="h1" variant="section-title-lg">
                  {t("onboarding:wizard.steps.company.title")}
                </Text>
                <Text variant="secondary" className="mt-2 text-sm">
                  {t("onboarding:wizard.steps.company.description")}
                </Text>
              </div>
              <StepIllustration step="company" />
            </div>

            <div className="relative mt-6">
              <Label
                htmlFor="companySearch"
                value={t("onboarding:wizard.steps.company.input_label")}
              />
              <div className="relative mt-2">
                <TextInput
                  id="companySearch"
                  value={companySearch}
                  onChange={(event) => {
                    setCompanySearch(event.target.value);
                    if (event.target.value !== company.name) {
                      setCompany((current) => ({
                        ...current,
                        name: "",
                        organizationNumber: "",
                      }));
                    }
                  }}
                  placeholder={t("onboarding:wizard.steps.company.placeholder")}
                  autoComplete="off"
                  autoFocus
                />
                {isSearching ? (
                  <Spinner className="absolute right-3 top-2.5" size="sm" />
                ) : null}
              </div>

              {searchResults.length > 0 ? (
                <ul className="absolute z-20 mt-1 max-h-72 w-full overflow-y-auto rounded-lg border border-border bg-surface shadow-lg">
                  {searchResults.map((result) => {
                    const address =
                      result.forretningsadresse ?? result.postadresse;
                    return (
                      <li key={result.organisasjonsnummer}>
                        <button
                          type="button"
                          className="w-full border-b border-border px-4 py-3 text-left last:border-0 hover:bg-surface-hover"
                          onClick={() => selectCompany(result)}
                        >
                          <span className="block font-medium text-text">
                            {result.navn}
                          </span>
                          <span className="block text-sm text-secondary">
                            {t("onboarding:wizard.steps.company.org_number", {
                              number: result.organisasjonsnummer,
                            })}
                          </span>
                          {address ? (
                            <span className="block text-xs text-muted">
                              {[
                                address.adresse?.join(", "),
                                address.postnummer,
                                address.poststed,
                              ]
                                .filter(Boolean)
                                .join(" ")}
                            </span>
                          ) : null}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              ) : null}

              {searchFailed ? (
                <Text className="mt-2 text-sm text-danger">
                  {t("onboarding:wizard.steps.company.search_error")}
                </Text>
              ) : null}
            </div>

            {company.name ? (
              <div className="mt-5 flex gap-3 rounded-lg border border-success/30 bg-success-muted p-4">
                <HiOutlineOfficeBuilding className="mt-0.5 h-5 w-5 flex-shrink-0 text-success" />
                <div>
                  <Text className="font-medium">{company.name}</Text>
                  <Text variant="secondary" className="text-sm">
                    {t("onboarding:wizard.steps.company.org_number", {
                      number: company.organizationNumber,
                    })}
                  </Text>
                  {company.primaryAddress ? (
                    <Text variant="muted" className="text-sm">
                      {[
                        company.primaryAddress.addressLine,
                        company.primaryAddress.postCode,
                        company.primaryAddress.city,
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    </Text>
                  ) : null}
                </div>
              </div>
            ) : null}

            {errorMessage ? (
              <div className="mt-5 rounded-lg border border-danger/30 bg-danger-muted p-3 text-sm text-danger">
                {errorMessage}
              </div>
            ) : null}

            <div className="mt-8 flex justify-between gap-3">
              <Button
                variant="secondary"
                icon={<HiOutlineArrowLeft className="h-4 w-4" />}
                disabled={isSubmitting}
                onClick={() => setStep("workspace")}
              >
                {t("onboarding:wizard.back")}
              </Button>
              <Button
                variant="primary"
                icon={<HiOutlineArrowRight className="h-4 w-4" />}
                iconPosition="right"
                disabled={
                  !company.name || !company.organizationNumber || isSubmitting
                }
                onClick={() => void saveCompanyStep()}
              >
                {isSubmitting
                  ? t("onboarding:wizard.saving")
                  : t("onboarding:wizard.continue")}
              </Button>
            </div>
          </section>
        ) : null}

        {step === "accounting" ? (
          <section>
            <div className="flex items-start justify-between gap-6">
              <div>
                <div className="flex items-center gap-3">
                  <Text as="h1" variant="section-title-lg">
                    {t("onboarding:wizard.steps.accounting.title")}
                  </Text>
                  <span className="rounded-full bg-surface-muted px-2.5 py-1 text-xs text-muted">
                    {t("onboarding:wizard.optional")}
                  </span>
                </div>
                <Text variant="secondary" className="mt-2 text-sm">
                  {t("onboarding:wizard.steps.accounting.description")}
                </Text>
              </div>
              <StepIllustration step="accounting" />
            </div>

            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <div className="flex items-center gap-1.5">
                  <Label
                    htmlFor="invoiceEmail"
                    value={t("common:invoice_email")}
                  />
                  <Tooltip
                    content={t("common:tooltips.invoice_email")}
                    trigger="click"
                  >
                    <button
                      type="button"
                      aria-label={`${t("common:help")}: ${t("common:invoice_email")}`}
                      className="text-muted hover:text-text"
                    >
                      <HiOutlineInformationCircle className="h-4 w-4" />
                    </button>
                  </Tooltip>
                </div>
                <TextInput
                  id="invoiceEmail"
                  className="mt-2"
                  type="email"
                  value={company.invoiceEmail ?? ""}
                  onChange={(event) =>
                    setCompany((current) => ({
                      ...current,
                      invoiceEmail: event.target.value,
                    }))
                  }
                />
              </div>
              <div>
                <div className="flex items-center gap-1.5">
                  <Label
                    htmlFor="accountingEmail"
                    value={t("common:accounting_email")}
                  />
                  <Tooltip
                    content={t("common:tooltips.accounting_email")}
                    trigger="click"
                  >
                    <button
                      type="button"
                      aria-label={`${t("common:help")}: ${t("common:accounting_email")}`}
                      className="text-muted hover:text-text"
                    >
                      <HiOutlineInformationCircle className="h-4 w-4" />
                    </button>
                  </Tooltip>
                </div>
                <TextInput
                  id="accountingEmail"
                  className="mt-2"
                  type="email"
                  value={company.accountingSettings?.accountingEmail ?? ""}
                  onChange={(event) =>
                    setCompany((current) => ({
                      ...current,
                      accountingSettings: {
                        ...current.accountingSettings,
                        accountingEmail: event.target.value,
                      },
                    }))
                  }
                />
              </div>
              <div>
                <Label htmlFor="paymentDays" value={t("common:payment_days")} />
                <TextInput
                  id="paymentDays"
                  className="mt-2"
                  type="number"
                  min={0}
                  value={company.paymentTerms.paymentDays}
                  onChange={(event) =>
                    updatePaymentTerms(
                      "paymentDays",
                      Number(event.target.value),
                    )
                  }
                />
              </div>
              <div>
                <Label
                  htmlFor="accountNumber"
                  value={t("common:account_number")}
                />
                <TextInput
                  id="accountNumber"
                  className="mt-2"
                  value={company.paymentTerms.accountNumber}
                  onChange={(event) =>
                    updatePaymentTerms("accountNumber", event.target.value)
                  }
                />
              </div>
              <div>
                <Label htmlFor="iban" value={t("common:iban")} />
                <TextInput
                  id="iban"
                  className="mt-2"
                  value={company.paymentTerms.iban}
                  onChange={(event) =>
                    updatePaymentTerms("iban", event.target.value)
                  }
                />
              </div>
              <div>
                <Label htmlFor="swift" value={t("common:swift")} />
                <TextInput
                  id="swift"
                  className="mt-2"
                  value={company.paymentTerms.swift}
                  onChange={(event) =>
                    updatePaymentTerms("swift", event.target.value)
                  }
                />
              </div>
            </div>

            <div className="mt-4">
              <div className="flex items-center justify-between gap-4 rounded-lg border border-border bg-surface-muted/30 p-4">
                <div>
                  <Text className="font-medium">
                    {t("common:is_vat_registered")}
                  </Text>
                  <Text variant="muted" className="text-xs">
                    {t("onboarding:wizard.steps.accounting.vat_description")}
                  </Text>
                </div>
                <ToggleSwitch
                  checked={company.paymentTerms.isVatRegistered}
                  onChange={() =>
                    updatePaymentTerms(
                      "isVatRegistered",
                      !company.paymentTerms.isVatRegistered,
                    )
                  }
                  ariaLabel={t("common:is_vat_registered")}
                />
              </div>
            </div>

            {errorMessage ? (
              <div className="mt-5 rounded-lg border border-danger/30 bg-danger-muted p-3 text-sm text-danger">
                {errorMessage}
              </div>
            ) : null}

            <div className="mt-8 flex flex-col-reverse justify-between gap-3 sm:flex-row">
              <Button
                variant="secondary"
                icon={<HiOutlineArrowLeft className="h-4 w-4" />}
                disabled={isSubmitting}
                onClick={() => setStep("company")}
              >
                {t("onboarding:wizard.back")}
              </Button>
              <div className="flex flex-col-reverse gap-3 sm:flex-row">
                <Button
                  variant="secondary"
                  disabled={isSubmitting}
                  onClick={() => void finishOnboarding(false)}
                >
                  {t("onboarding:wizard.skip")}
                </Button>
                <Button
                  variant="primary"
                  icon={<HiOutlineArrowRight className="h-4 w-4" />}
                  iconPosition="right"
                  disabled={isSubmitting}
                  onClick={() => void finishOnboarding(true)}
                >
                  {isSubmitting
                    ? t("onboarding:wizard.saving")
                    : t("onboarding:wizard.finish")}
                </Button>
              </div>
            </div>
          </section>
        ) : null}
      </div>
    </div>
  );
};

export default OnboardingWizard;
